import { useMemo } from "react";
import { UseQueryResult, useQuery } from "react-query";

import { AxiosError } from "axios";

import { GridType, LOCATION_LOOKUP_COUNTRY, NamedOption, getRegions } from "api/map";

import {
  GRID_FORMS,
  GridForm,
  GridLevel,
  JumpToLocationSelection,
  gridFormFor
} from "./jumpToLocationGrids";

const QUERY_KEY = "jumpToLocation";

// The grid tables barely move, and the service caches them too, so every level keeps its
// list instead of re-asking each time the option is opened. Not forever, though: the
// region list also carries which survey system each region uses, and a region list read
// while the grid tables were unreadable has to be able to correct itself without a page
// reload.
const REFERENCE_STALE_TIME = 10 * 60 * 1000;

// The service answers 503 when its reference tables are missing or unreadable. A 404 means
// the lookup routes are not there at all — an older service during a deploy or a rollback.
// Both mean "no location data here", so neither should offer a Retry that cannot succeed.
const REFERENCE_UNAVAILABLE_STATUSES = [404, 503];

// How many levels the longest form has, counted from the forms rather than written out
// again here. The slots are fixed, so the same number of queries runs on every render
// whichever form is showing, and a form with fewer levels leaves the last slots idle.
const LEVEL_SLOTS = Math.max(
  ...Object.values(GRID_FORMS).map((form) => form.levels.length)
);

// One query below per slot, written out because hooks cannot be called in a loop. A form
// that grows a fifth level would otherwise come back one level short and simply not draw
// its last dropdown, so the count is checked here instead of going unnoticed.
const LEVEL_QUERIES = 4;

if (process.env.NODE_ENV !== "production" && LEVEL_QUERIES < LEVEL_SLOTS) {
  throw new Error(
    `Jump to Location has a form with ${LEVEL_SLOTS} levels and only ` +
      `${LEVEL_QUERIES} level queries — add a query for the level that was added`
  );
}

export interface OptionLevel {
  options: NamedOption[];
  isLoading: boolean;
  // True while any request is in flight, including a Retry after a failure. isLoading
  // covers only the first one, so without this a Retry looks like nothing happened.
  isFetching: boolean;
  isError: boolean;
  isUnavailable: boolean;
  refetch: () => void;
}

export interface JumpToLocationOptions {
  regions: OptionLevel;
  /** The survey system the chosen region uses, once the region list has said so. */
  gridType: Nullable<GridType>;
  form: Nullable<GridForm>;
  /** One entry per level of that form, in the order the form draws them. */
  levels: OptionLevel[];
}

function toLevel(query: UseQueryResult<NamedOption[]>): OptionLevel {
  const status = (query.error as AxiosError)?.response?.status;
  return {
    options: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    isUnavailable:
      status !== undefined && REFERENCE_UNAVAILABLE_STATUSES.includes(status),
    refetch: () => {
      query.refetch();
    }
  };
}

/**
 * One level of whichever form is showing. The level says what fills it and which choices it
 * sits under, so the query is enabled only once those are made and is keyed by them. A slot
 * with no level in it asks for nothing.
 */
function useLevelQuery(
  level: Nullable<GridLevel>,
  gridType: Nullable<GridType>,
  selection: JumpToLocationSelection,
  enabled: boolean
): OptionLevel {
  const parents = level?.parents ?? [];
  const parentValues = parents.map((parent) => selection[parent]);
  const query = useQuery(
    [QUERY_KEY, gridType, level?.field ?? "idle", selection.region, ...parentValues],
    () => level.fetch(selection),
    {
      enabled: enabled && Boolean(level) && parentValues.every(Boolean),
      staleTime: REFERENCE_STALE_TIME,
      retry: false
    }
  );

  return toLevel(query);
}

/**
 * The lists behind the Jump to Location cascade. The region list comes first and names the
 * survey system, and the levels under it are whichever ones that system has. Each level is
 * enabled only once the levels it sits under have a value, which gives the "populated in
 * order" behaviour for free and keeps a slow answer for an abandoned parent from filling a
 * child list.
 */
export default function useJumpToLocationOptions(
  selection: JumpToLocationSelection,
  enabled: boolean
): JumpToLocationOptions {
  const regions = useQuery([QUERY_KEY, "regions"], getRegions, {
    enabled,
    staleTime: REFERENCE_STALE_TIME,
    retry: false
  });

  // Only the US grids are offered here, and the region list is the one place a region's
  // country is named, so anywhere else is dropped before a region is offered or read.
  // Memoized so the list keeps one identity for as long as the answer does, which is what
  // the options drawn from it are memoized against.
  const offered = useMemo(
    () =>
      (regions.data ?? []).filter((region) => region.country === LOCATION_LOOKUP_COUNTRY),
    [regions.data]
  );

  // Which form to draw is answered from the region list, with no request of its own. Until
  // it lands there is no form, so none of the levels below asks for anything.
  const chosenRegion = offered.find((option) => option.id === selection.region);
  const gridType = chosenRegion?.gridType ?? null;
  const form = gridFormFor(gridType);
  const levels = form?.levels ?? [];

  const first = useLevelQuery(levels[0] ?? null, gridType, selection, enabled);
  const second = useLevelQuery(levels[1] ?? null, gridType, selection, enabled);
  const third = useLevelQuery(levels[2] ?? null, gridType, selection, enabled);
  const fourth = useLevelQuery(levels[3] ?? null, gridType, selection, enabled);

  // One entry per level the form has, and never an entry with no query behind it. A form
  // that grows past these queries comes back one level short until a query is added for
  // it, rather than handing the cascade a slot with nothing in it.
  const slots = [first, second, third, fourth].slice(0, LEVEL_SLOTS);

  return {
    regions: { ...toLevel(regions), options: offered },
    gridType,
    form,
    levels: slots.slice(0, levels.length)
  };
}
